import { api } from "./client";

export interface ClinicStats {
  id: string;
  name: string;
  legal_name: string | null;
  license_number: string | null;
  address: string | null;
  phone: string | null;
  inn: string | null;
  created_at: string;
  user_count: number;
  patient_count: number;
  examination_count: number;
  completed_exam_count: number;
  last_exam_at: string | null;
  billing_status: string | null;
  trial_until: string | null;
  paid_until: string | null;
  monthly_fee: number | null;
}

export interface GlobalStats {
  clinic_count: number;
  active_clinic_count: number;
  trial_clinic_count: number;
  blocked_clinic_count: number;
  total_users: number;
  total_patients: number;
  total_examinations: number;
  total_completed: number;
  total_revenue_paid: number;
  total_revenue_outstanding: number;
}

export interface BillingPlan {
  id: string;
  clinic_id: string;
  plan_name: string;
  monthly_fee: number;
  currency: string;
  trial_until: string | null;
  paid_until: string | null;
  status: string;
  notes: string | null;
}

export interface Invoice {
  id: string;
  clinic_id: string;
  period_from: string;
  period_to: string;
  amount: number;
  currency: string;
  paid_amount: number;
  paid_at: string | null;
  method: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

export interface ClinicUserSummary {
  id: string;
  email: string;
  full_name: string;
  role: string;
  specialty: string | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface ClinicPatientSummary {
  id: string;
  card_number: string;
  family_name: string;
  given_name: string;
  birth_date: string | null;
  sex: string | null;
  workplace: string | null;
  position: string | null;
  created_at: string;
}

export interface ClinicExamSummary {
  id: string;
  exam_number: number;
  type: string;
  status: string;
  final_verdict: string | null;
  job_title: string | null;
  patient_id: string;
  patient_name: string;
  patient_card: string;
  created_at: string;
}

export interface ClinicFormSummary {
  id: string;
  examination_id: string;
  template: string;
  form_number: string;
  generated_at: string;
  revoked_at: string | null;
  pdf_object_key: string;
  patient_name: string;
  patient_card: string;
}

export interface ClinicAuditEntry {
  id: string;
  created_at: string;
  actor_id: string | null;
  actor_role: string | null;
  actor_specialty: string | null;
  permission: string;
  verb: string;
  target_type: string | null;
  target_id: string | null;
  diff_keys: string[] | null;
  ip_address: string | null;
}

export interface ClinicCreateRequest {
  name: string;
  legal_name?: string;
  license_number?: string;
  address?: string;
  phone?: string;
  inn?: string;
  card_prefix?: string;
  admin_login: string;
  admin_password: string;
  admin_full_name: string;
  plan_name?: string;
  monthly_fee: number;
  currency?: string;
  trial_days: number;
}

export interface BillingPlanUpdate {
  plan_name?: string;
  monthly_fee?: number;
  currency?: string;
  trial_until?: string | null;
  paid_until?: string | null;
  status?: string;
  notes?: string;
}

export interface InvoiceCreateRequest {
  period_from: string;
  period_to: string;
  amount: number;
  currency?: string;
  notes?: string;
}

export interface InvoicePayRequest {
  amount: number;
  method: string;
  notes?: string;
}

// ─── API calls ───────────────────────────────────────────────────────────
export async function getGlobalStats(): Promise<GlobalStats> {
  const { data } = await api.get<GlobalStats>("/admin/stats");
  return data;
}

export async function listClinics(): Promise<ClinicStats[]> {
  const { data } = await api.get<{ items: ClinicStats[]; total: number }>(
    "/admin/clinics",
  );
  return data.items;
}

export async function getClinic(id: string): Promise<ClinicStats> {
  const { data } = await api.get<ClinicStats>(`/admin/clinics/${id}`);
  return data;
}

export async function createClinic(payload: ClinicCreateRequest): Promise<ClinicStats> {
  const { data } = await api.post<ClinicStats>("/admin/clinics", payload);
  return data;
}

export async function updateClinic(
  id: string,
  payload: Partial<ClinicCreateRequest>,
): Promise<ClinicStats> {
  const { data } = await api.patch<ClinicStats>(`/admin/clinics/${id}`, payload);
  return data;
}

export async function toggleClinicBlock(id: string): Promise<BillingPlan> {
  const { data } = await api.post<BillingPlan>(`/admin/clinics/${id}/block`);
  return data;
}

export async function listClinicUsers(id: string): Promise<ClinicUserSummary[]> {
  const { data } = await api.get<ClinicUserSummary[]>(`/admin/clinics/${id}/users`);
  return data;
}

export async function listClinicPatients(id: string): Promise<ClinicPatientSummary[]> {
  const { data } = await api.get<ClinicPatientSummary[]>(`/admin/clinics/${id}/patients`);
  return data;
}

export async function listClinicExams(id: string): Promise<ClinicExamSummary[]> {
  const { data } = await api.get<ClinicExamSummary[]>(`/admin/clinics/${id}/examinations`);
  return data;
}

export async function listClinicAudit(id: string): Promise<ClinicAuditEntry[]> {
  const { data } = await api.get<ClinicAuditEntry[]>(`/admin/clinics/${id}/audit`);
  return data;
}

export async function listClinicForms(id: string): Promise<ClinicFormSummary[]> {
  const { data } = await api.get<ClinicFormSummary[]>(`/admin/clinics/${id}/forms`);
  return data;
}

export async function downloadClinicForm(
  clinicId: string,
  formId: string,
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `/admin/clinics/${clinicId}/forms/${formId}/pdf`,
    { responseType: "blob" },
  );
  return data;
}

export async function getClinicBilling(
  id: string,
): Promise<{ plan: BillingPlan; invoices: Invoice[] }> {
  const { data } = await api.get(`/admin/clinics/${id}/billing`);
  return data as { plan: BillingPlan; invoices: Invoice[] };
}

export async function updateBillingPlan(
  id: string,
  payload: BillingPlanUpdate,
): Promise<BillingPlan> {
  const { data } = await api.patch<BillingPlan>(
    `/admin/clinics/${id}/billing`,
    payload,
  );
  return data;
}

export async function createInvoice(
  clinicId: string,
  payload: InvoiceCreateRequest,
): Promise<Invoice> {
  const { data } = await api.post<Invoice>(
    `/admin/clinics/${clinicId}/billing/invoice`,
    payload,
  );
  return data;
}

export async function payInvoice(
  clinicId: string,
  invoiceId: string,
  payload: InvoicePayRequest,
): Promise<Invoice> {
  const { data } = await api.post<Invoice>(
    `/admin/clinics/${clinicId}/billing/invoice/${invoiceId}/pay`,
    payload,
  );
  return data;
}

export async function deleteInvoice(
  clinicId: string,
  invoiceId: string,
): Promise<void> {
  await api.delete(`/admin/clinics/${clinicId}/billing/invoice/${invoiceId}`);
}
